<template>
    <v-card class="viewer-360">
        <div class="viewer-360__header">
            <span class="viewer-360__title">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" style="margin-right:6px;vertical-align:middle">
                    <path d="M12 4C7.6 4 4 7.6 4 12s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm3.5 11.5l-5-2.5V6h1.5v6.2l4 2-0.5 1.3z"/>
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z"/>
                </svg>
                360° 뷰어
            </span>
            <div class="viewer-360__header-right">
                <span class="viewer-360__hint">← 좌우로 드래그 →</span>
                <v-btn icon small @click="$emit('close')">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
        </div>

        <div
            class="viewer-360__stage"
            ref="stage"
            :class="{ 'is-dragging': isDragging, 'is-loaded': loaded }"
            @pointerdown.prevent="onPointerDown"
            @pointermove.prevent="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @touchstart.prevent
        >
            <div v-if="!loaded" class="viewer-360__loading">
                <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                <div class="mt-2">이미지 로딩 중... {{ loadedCount }}/{{ frames.length }}</div>
            </div>
            <img
                v-show="loaded"
                ref="viewImg"
                :src="currentFrameUrl"
                draggable="false"
                class="viewer-360__img"
                alt="360° 제품 이미지"
            />
        </div>

        <div class="viewer-360__controls">
            <div class="viewer-360__frame-info">
                {{ currentIndex + 1 }} / {{ frames.length }}
            </div>
            <v-btn text small class="viewer-360__play-btn" @click="toggleAutoplay">
                <v-icon left small>{{ playing ? 'mdi-pause' : 'mdi-play' }}</v-icon>
                {{ playing ? '정지' : '자동재생' }}
            </v-btn>
            <div class="viewer-360__sensitivity">
                <span>감도</span>
                <v-slider
                    v-model="sensitivity"
                    :min="2"
                    :max="20"
                    :step="1"
                    dense
                    hide-details
                    style="width:80px"
                ></v-slider>
            </div>
        </div>
    </v-card>
</template>

<script>
    export default {
        name: 'Product360Viewer',

        props: {
            frames: {
                type: Array,
                required: true,
                default: () => [],
            },
        },

        data() {
            return {
                currentIndex: 0,
                isDragging: false,
                startX: 0,
                startIndex: 0,
                loaded: false,
                loadedCount: 0,
                preloadedImages: [],
                sensitivity: 5,
                playing: false,
                autoplayTimer: null,
                rafId: null,
            };
        },

        computed: {
            currentFrameUrl() {
                if (!this.frames.length) return '';
                return this.frames[this.currentIndex];
            },
        },

        mounted() {
            this.preloadFrames();
        },

        beforeDestroy() {
            this.stopAutoplay();
            if (this.rafId) cancelAnimationFrame(this.rafId);
        },

        methods: {
            preloadFrames() {
                if (!this.frames.length) return;
                this.loadedCount = 0;
                this.preloadedImages = [];

                this.frames.forEach((url, i) => {
                    const img = new Image();
                    img.onload = () => {
                        this.loadedCount++;
                        if (this.loadedCount >= this.frames.length) {
                            this.loaded = true;
                        }
                    };
                    img.onerror = () => {
                        this.loadedCount++;
                        if (this.loadedCount >= this.frames.length) {
                            this.loaded = true;
                        }
                    };
                    img.src = url;
                    this.preloadedImages.push(img);
                });
            },

            onPointerDown(e) {
                if (!this.loaded) return;
                this.$refs.stage.setPointerCapture(e.pointerId);
                this.isDragging = true;
                this.startX = e.clientX;
                this.startIndex = this.currentIndex;
                this.stopAutoplay();
            },

            onPointerMove(e) {
                if (!this.isDragging || !this.loaded) return;
                const dx = e.clientX - this.startX;
                const frameDelta = Math.floor(dx / this.sensitivity);
                const count = this.frames.length;
                this.currentIndex = ((this.startIndex - frameDelta) % count + count) % count;
            },

            onPointerUp() {
                this.isDragging = false;
            },

            toggleAutoplay() {
                if (this.playing) {
                    this.stopAutoplay();
                } else {
                    this.startAutoplay();
                }
            },

            startAutoplay() {
                this.playing = true;
                this.autoplayTimer = setInterval(() => {
                    const count = this.frames.length;
                    this.currentIndex = (this.currentIndex + 1) % count;
                }, 80);
            },

            stopAutoplay() {
                this.playing = false;
                if (this.autoplayTimer) {
                    clearInterval(this.autoplayTimer);
                    this.autoplayTimer = null;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .viewer-360 {
        user-select: none;

        &__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
            border-bottom: 1px solid #eee;
        }

        &__title {
            font-size: 15px;
            font-weight: 600;
            color: #19253d;
            display: flex;
            align-items: center;
        }

        &__header-right {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        &__hint {
            font-size: 12px;
            color: #888;
        }

        &__stage {
            position: relative;
            width: 100%;
            background: #f8f8f8;
            min-height: 400px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: grab;
            overflow: hidden;

            &.is-dragging {
                cursor: grabbing;
            }

            &.is-loaded {
                background: #fff;
            }
        }

        &__loading {
            text-align: center;
            color: #666;
            font-size: 13px;
        }

        &__img {
            width: 100%;
            height: auto;
            max-height: 500px;
            object-fit: contain;
            display: block;
            pointer-events: none;
        }

        &__controls {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 16px;
            border-top: 1px solid #eee;
            background: #fafafa;
        }

        &__frame-info {
            font-size: 12px;
            color: #999;
            min-width: 50px;
        }

        &__play-btn {
            font-size: 12px;
        }

        &__sensitivity {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: #888;
        }
    }
</style>
